/** Sector rotation dashboard — ETF analysis, stage detection, alerts. */

export const SECTOR_DASHBOARD_ETFS = [
  { etf: "XLK", name: "Technology" },
  { etf: "XLF", name: "Financials" },
  { etf: "XLE", name: "Energy" },
  { etf: "XLV", name: "Healthcare" },
  { etf: "XLP", name: "Consumer Staples" },
  { etf: "XLY", name: "Consumer Discretionary" },
  { etf: "XLI", name: "Industrials" },
  { etf: "XLB", name: "Materials" },
  { etf: "XLU", name: "Utilities" },
];

export const SECTOR_TICKER_PLAYS = {
  XLK: ["AAPL", "MSFT", "NVDA", "AMD", "GOOGL", "META", "AVGO", "CRM", "ORCL", "ADBE"],
  XLF: ["JPM", "BAC", "WFC", "GS", "MS", "BLK", "AXP", "USB", "PNC", "COF"],
  XLE: ["XOM", "CVX", "COP", "SLB", "EOG", "PXD", "MPC", "PSX", "VLO", "HAL"],
  XLV: ["JNJ", "UNH", "LLY", "MRK", "ABBV", "PFE", "TMO", "ABT", "DHR", "BMY"],
  XLP: ["PG", "KO", "PEP", "COST", "WMT", "MO", "PM", "CL", "GIS", "K"],
  XLY: ["AMZN", "TSLA", "HD", "MCD", "NKE", "SBUX", "TJX", "BKNG", "LOW", "CMG"],
  XLI: ["CAT", "HON", "UPS", "BA", "GE", "MMM", "LMT", "RTX", "DE", "EMR"],
  XLB: ["LIN", "APD", "SHW", "ECL", "NEM", "FCX", "NUE", "VMC", "MLM", "CF"],
  XLU: ["NEE", "DUK", "SO", "D", "AEP", "EXC", "SRE", "PCG", "ED", "ES"],
};

export const STAGE_KEYS = {
  HOT: "HOT",
  ROTATING: "ROTATING",
  ACCUMULATION: "ACCUMULATION",
  NEUTRAL: "NEUTRAL",
  COOLING: "COOLING",
  OUT: "OUT",
};

export const STAGE_DISPLAY = {
  HOT: { label: "🚀 HOT SECTOR", color: "#22c55e", sort: 1 },
  ROTATING: { label: "⚡ ROTATING IN", color: "#eab308", sort: 2 },
  ACCUMULATION: { label: "🔍 ACCUMULATION", color: "#3b82f6", sort: 3 },
  NEUTRAL: { label: "NEUTRAL", color: "#94a3b8", sort: 4 },
  COOLING: { label: "📉 COOLING", color: "#f97316", sort: 5 },
  OUT: { label: "❄ ROTATING OUT", color: "#ef4444", sort: 6 },
};

export const SECTOR_CACHE_MS = 15 * 60 * 1000;

export const SECTOR_EMOJI_BY_ETF = {
  XLV: "🏥",
  XLK: "💡",
  XLF: "💰",
  XLU: "⚡",
  XLE: "🛢",
  XLI: "🏭",
  XLP: "🛒",
  XLY: "🛍",
  XLB: "🪨",
};

const ROTATION_TICKER_LISTS = {
  XLK: ["AAPL", "MSFT", "NVDA", "AMD", "GOOGL", "META", "AVGO", "CRM", "ORCL", "ADBE", "INTC", "QCOM", "TXN", "MU", "AMAT"],
  XLV: ["JNJ", "UNH", "LLY", "MRK", "ABBV", "PFE", "TMO", "ABT", "DHR", "BMY", "AMGN", "GILD", "ISRG", "SYK", "BSX", "MDT"],
  XLF: ["JPM", "BAC", "WFC", "GS", "MS", "BLK", "AXP", "USB", "PNC", "COF", "SCHW", "BX", "KKR", "MA", "V", "AIG", "MET", "PRU"],
  XLE: ["XOM", "CVX", "COP", "SLB", "EOG", "PXD", "MPC", "PSX", "VLO", "HAL", "OXY", "DVN", "FANG", "HES"],
  XLP: ["PG", "KO", "PEP", "COST", "WMT", "MO", "PM", "CL", "GIS", "K", "EL", "CHD", "HSY", "CAG", "CPB"],
  XLY: ["AMZN", "TSLA", "HD", "MCD", "NKE", "SBUX", "TJX", "BKNG", "LOW", "CMG", "YUM", "DHI", "LEN"],
  XLI: ["CAT", "HON", "UPS", "BA", "GE", "MMM", "LMT", "RTX", "DE", "EMR", "ITW", "ETN", "PH", "ROK", "AME"],
  XLB: ["LIN", "APD", "SHW", "ECL", "NEM", "FCX", "NUE", "VMC", "MLM", "CF", "MOS", "ALB", "DD"],
  XLU: ["NEE", "DUK", "SO", "D", "AEP", "EXC", "SRE", "PCG", "ED", "ES", "AWK", "PPL", "WEC", "ETR", "FE"],
};

export const SECTOR_ROTATION_TICKER_MAP = Object.fromEntries(
  Object.entries(ROTATION_TICKER_LISTS).flatMap(([etf, tickers]) =>
    tickers.map(t => [t, etf])
  )
);

export function getTickerRotationSectorInfo(ticker) {
  const sym = String(ticker || "").toUpperCase();
  const etf = SECTOR_ROTATION_TICKER_MAP[sym];
  if (!etf) return { sector: "Unknown", sectorEtf: null, sectorEmoji: null };
  const info = SECTOR_DASHBOARD_ETFS.find(s => s.etf === etf);
  return {
    sector: info?.name || etf,
    sectorEtf: etf,
    sectorEmoji: SECTOR_EMOJI_BY_ETF[etf] || "🔄",
  };
}

export function readLastScanResultsCache() {
  try {
    return JSON.parse(localStorage.getItem("lastScanResults") || "{}");
  } catch {
    return {};
  }
}

export function pickSectorAlertTickers(byEtf, watchlistSet, cachedScan = {}) {
  const alertStages = new Set([STAGE_KEYS.HOT, STAGE_KEYS.ROTATING]);
  const hotSectors = Object.values(byEtf || {})
    .filter(s => s && alertStages.has(s.stageKey))
    .sort((a, b) => {
      const sa = STAGE_DISPLAY[a.stageKey]?.sort ?? 99;
      const sb = STAGE_DISPLAY[b.stageKey]?.sort ?? 99;
      if (sa !== sb) return sa - sb;
      return (b.perf5vsSpy ?? -999) - (a.perf5vsSpy ?? -999);
    });

  const wl = watchlistSet instanceof Set
    ? watchlistSet
    : new Set((watchlistSet || []).map(s => String(s).toUpperCase()));

  const picks = [];
  for (const sector of hotSectors) {
    const tickers = SECTOR_TICKER_PLAYS[sector.etf] || [];
    const candidates = tickers.filter(t => !wl.has(String(t).toUpperCase()));
    candidates.sort((a, b) => {
      const cmfA = cachedScan[a]?.cmf;
      const cmfB = cachedScan[b]?.cmf;
      if (cmfA != null && cmfB != null) return cmfB - cmfA;
      if (cmfA != null) return -1;
      if (cmfB != null) return 1;
      return a.localeCompare(b);
    });
    for (const symbol of candidates.slice(0, 3)) {
      picks.push({
        symbol,
        sectorEtf: sector.etf,
        sectorName: sector.name,
        sectorStage: sector.stageKey,
        sectorEmoji: SECTOR_EMOJI_BY_ETF[sector.etf] || "🔄",
        perf5vsSpy: sector.perf5vsSpy,
      });
    }
  }
  return picks.slice(0, 9);
}

export function buildRotationScanBannerMessage(byEtf, alertCount = 0) {
  const alertStages = new Set([STAGE_KEYS.HOT, STAGE_KEYS.ROTATING]);
  const active = Object.values(byEtf || {})
    .filter(s => s && alertStages.has(s.stageKey))
    .sort((a, b) => (STAGE_DISPLAY[a.stageKey]?.sort ?? 99) - (STAGE_DISPLAY[b.stageKey]?.sort ?? 99));

  if (!active.length) return null;

  const parts = active.map(s => {
    const emoji = SECTOR_EMOJI_BY_ETF[s.etf] || "";
    const label = s.stageKey === STAGE_KEYS.HOT ? "HOT" : "ROTATING IN";
    const perf = s.perf5vsSpy != null
      ? ` (${s.perf5vsSpy >= 0 ? "+" : ""}${s.perf5vsSpy.toFixed(2)}% vs SPY)`
      : "";
    return `${emoji} ${s.name} ${label}${perf}`;
  });

  const scanNote = alertCount > 0
    ? ` · ${alertCount} sector play${alertCount !== 1 ? "s" : ""} auto-scanned below ↓`
    : "";
  return `🔄 ROTATION ALERT: ${parts.join(" · ")}${scanNote}`;
}

export function formatSectorAlertStageLabel(stageKey) {
  if (stageKey === STAGE_KEYS.HOT) return "HOT SECTOR";
  if (stageKey === STAGE_KEYS.ROTATING) return "ROTATING IN";
  if (stageKey === STAGE_KEYS.ACCUMULATION) return "ACCUMULATING";
  return STAGE_DISPLAY[stageKey]?.label?.replace(/^[^\s]+\s/, "") || stageKey;
}

function sma(arr, n) {
  if (!arr || arr.length < n) return null;
  return arr.slice(0, n).reduce((s, v) => s + v, 0) / n;
}

function emaArr(closes, period) {
  if (!closes || closes.length < period) return [];
  const k = 2 / (period + 1);
  const chrono = [...closes].reverse();
  let e = chrono.slice(0, period).reduce((a, b) => a + b, 0) / period;
  const out = [e];
  for (let i = period; i < chrono.length; i++) {
    e = chrono[i] * k + e * (1 - k);
    out.push(e);
  }
  return out.reverse();
}

function calcRSI(closes, period = 14) {
  const rev = [...closes].reverse();
  if (rev.length < period + 1) return null;
  let ag = 0, al = 0;
  for (let i = 1; i <= period; i++) {
    const d = rev[i] - rev[i - 1];
    if (d > 0) ag += d; else al -= d;
  }
  ag /= period; al /= period;
  for (let i = period + 1; i < rev.length; i++) {
    const d = rev[i] - rev[i - 1];
    ag = (ag * (period - 1) + Math.max(d, 0)) / period;
    al = (al * (period - 1) + Math.max(-d, 0)) / period;
  }
  return al === 0 ? 100 : 100 - 100 / (1 + ag / al);
}

function calcCMF(h, l, c, v, n = 20) {
  if (!c || c.length < n) return null;
  let mfv = 0, vol = 0;
  for (let i = 0; i < n; i++) {
    const hl = h[i] - l[i];
    if (!hl) continue;
    mfv += ((c[i] - l[i]) - (h[i] - c[i])) / hl * (v[i] || 0);
    vol += v[i] || 0;
  }
  return vol ? mfv / vol : 0;
}

function cmfAtOffset(h, l, c, v, offset) {
  if (!c || c.length < offset + 20) return null;
  return calcCMF(h.slice(offset), l.slice(offset), c.slice(offset), v.slice(offset), 20);
}

function calcBB(closes, n = 20) {
  if (!closes || closes.length < n + 5) return { isSqueeze: false };
  const bwOf = (sl) => {
    const m = sl.reduce((s, v) => s + v, 0) / sl.length;
    const sd = Math.sqrt(sl.reduce((s, v) => s + (v - m) ** 2, 0) / sl.length);
    return m > 0 ? (4 * sd) / m : 0;
  };
  const days = Math.min(closes.length, 60);
  const bws = [];
  for (let i = 0; i + n <= days; i++) bws.push(bwOf(closes.slice(i, i + n)));
  if (!bws.length) return { isSqueeze: false };
  const cur = bws[0];
  const mn = Math.min(...bws);
  return { isSqueeze: cur <= mn * 1.12 };
}

function obvSeries(closes, volumes) {
  if (!closes?.length || !volumes?.length) return null;
  const out = [0];
  for (let i = 0; i < closes.length - 1; i++) {
    let prev = out[out.length - 1];
    if (closes[i] > closes[i + 1]) prev += volumes[i] || 0;
    else if (closes[i] < closes[i + 1]) prev -= volumes[i] || 0;
    out.push(prev);
  }
  return out;
}

function pctChangeFrom(closes, days) {
  if (!closes || closes.length <= days || !closes[days]) return null;
  return ((closes[0] - closes[days]) / closes[days]) * 100;
}

function relPerfVsSpy(etfCloses, spyCloses, days) {
  const etfPct = pctChangeFrom(etfCloses, days);
  const spyPct = pctChangeFrom(spyCloses, days);
  if (etfPct == null || spyPct == null) return null;
  return etfPct - spyPct;
}

export function buildSectorEtfMetrics(raw, spyRaw) {
  const { closes, highs, lows, volumes } = raw || {};
  const spyCloses = spyRaw?.closes;
  if (!closes?.length || closes.length < 25) return null;

  const price = closes[0];
  const pctChange1d = closes[1] ? ((closes[0] - closes[1]) / closes[1]) * 100 : null;
  const perf5vsSpy = relPerfVsSpy(closes, spyCloses, 5);
  const perf10vsSpy = relPerfVsSpy(closes, spyCloses, 10);
  const cmf = calcCMF(highs, lows, closes, volumes);
  const cmf1 = cmfAtOffset(highs, lows, closes, volumes, 1);
  const cmf2 = cmfAtOffset(highs, lows, closes, volumes, 2);
  const cmf3 = cmfAtOffset(highs, lows, closes, volumes, 3);
  const cmf4 = cmfAtOffset(highs, lows, closes, volumes, 4);
  const cmf5 = cmfAtOffset(highs, lows, closes, volumes, 5);
  const cmfRising = cmf != null && cmf1 != null && cmf > cmf1;
  const cmfDeclining = cmf != null && cmf1 != null && cmf < cmf1;

  const obv = obvSeries(closes, volumes);
  const obvHigherLows = obv && obv.length >= 14
    ? Math.min(...obv.slice(0, 7)) > Math.min(...obv.slice(7, 14))
    : false;
  const obvLowerHighs = obv && obv.length >= 14
    ? Math.max(...obv.slice(0, 7)) < Math.max(...obv.slice(7, 14))
    : false;
  const obvDirection = obvHigherLows ? "HIGHER_LOWS" : obvLowerHighs ? "LOWER_HIGHS" : "MIXED";

  const avgVol20 = volumes && volumes.length >= 21
    ? volumes.slice(1, 21).reduce((s, v) => s + (v || 0), 0) / 20
    : null;
  const rvol = avgVol20 && avgVol20 > 0 ? (volumes[0] || 0) / avgVol20 : null;

  let upVol = 0, downVol = 0;
  for (let i = 0; i < 5 && i < closes.length - 1; i++) {
    const v = volumes[i] || 0;
    if (closes[i] > closes[i + 1]) upVol += v;
    else if (closes[i] < closes[i + 1]) downVol += v;
  }
  const volumeConfirming = upVol > downVol * 1.1;

  const ema20 = emaArr(closes, 20)[0];
  const aboveEma20 = ema20 != null && price > ema20;
  const rsi = calcRSI(closes);
  const bb = calcBB(closes);
  const priceFlat = pctChangeFrom(closes, 5) != null && Math.abs(pctChangeFrom(closes, 5)) <= 0.8;

  const cmfPositive3Days = [cmf, cmf1, cmf2].every(v => v != null && v > 0);
  const cmfWasNegative = [cmf3, cmf4, cmf5].some(v => v != null && v <= 0);

  return {
    price,
    pctChange1d,
    perf5vsSpy,
    perf10vsSpy,
    cmf,
    cmfRising,
    cmfDeclining,
    obvDirection,
    obvHigherLows,
    rvol,
    volumeConfirming,
    ema20,
    aboveEma20,
    rsi,
    bbSqueeze: !!bb?.isSqueeze,
    sparkCloses: closes.slice(0, 7).reverse(),
    cmfPositive3Days,
    cmfWasNegative,
    priceFlat,
  };
}

export function detectRotationStage(metrics, prevEntry = null) {
  if (!metrics) return { stageKey: STAGE_KEYS.NEUTRAL, stageSince: Date.now() };

  const prevStage = prevEntry?.stageKey || null;
  const prevSince = prevEntry?.stageSince || Date.now();
  const {
    cmf, perf5vsSpy, perf10vsSpy, aboveEma20, volumeConfirming,
    cmfPositive3Days, cmfWasNegative, obvHigherLows, priceFlat, cmfDeclining,
  } = metrics;

  let stageKey = STAGE_KEYS.NEUTRAL;

  // Force overrides — price action vs SPY takes precedence
  if (perf5vsSpy != null && perf5vsSpy >= 8) {
    stageKey = STAGE_KEYS.HOT;
  } else if (perf5vsSpy != null && perf5vsSpy >= 4 && cmf != null && cmf > -0.10) {
    stageKey = STAGE_KEYS.ROTATING;
  } else if (perf5vsSpy != null && perf5vsSpy >= 2 && cmf != null && cmf > -0.15) {
    stageKey = STAGE_KEYS.ACCUMULATION;
  } else if (perf5vsSpy != null && perf5vsSpy <= -2 && cmf != null && cmf < -0.05) {
    stageKey = STAGE_KEYS.OUT;
  } else if (cmf != null && cmf > 0.10 && perf5vsSpy != null && perf5vsSpy > 1.5
    && perf10vsSpy != null && perf10vsSpy > 2 && aboveEma20) {
    stageKey = STAGE_KEYS.HOT;
  } else if (cmf != null && cmf > 0.05 && aboveEma20
    && perf5vsSpy != null && perf5vsSpy > 0.5 && volumeConfirming) {
    stageKey = STAGE_KEYS.ROTATING;
  } else if (cmfPositive3Days && cmfWasNegative && obvHigherLows
    && volumeConfirming && !aboveEma20 && priceFlat) {
    stageKey = STAGE_KEYS.ACCUMULATION;
  } else if ((prevStage === STAGE_KEYS.HOT || prevStage === STAGE_KEYS.ROTATING)
    && (cmfDeclining || (perf5vsSpy != null && perf5vsSpy < 0.3))) {
    stageKey = STAGE_KEYS.COOLING;
  } else if (cmf != null && cmf < 0
    && perf5vsSpy != null && perf5vsSpy < 0
    && perf10vsSpy != null && perf10vsSpy < 0) {
    stageKey = STAGE_KEYS.OUT;
  }

  const stageSince = prevStage === stageKey ? prevSince : Date.now();
  const stageDays = Math.max(0, Math.floor((Date.now() - stageSince) / 86400000));

  return {
    stageKey,
    stageSince,
    stageDays,
    stageLabel: STAGE_DISPLAY[stageKey]?.label || "NEUTRAL",
    stageColor: STAGE_DISPLAY[stageKey]?.color || "#94a3b8",
  };
}

export function sortSectorsByStage(sectors) {
  return [...sectors].sort((a, b) => {
    const sa = STAGE_DISPLAY[a.stageKey]?.sort ?? 99;
    const sb = STAGE_DISPLAY[b.stageKey]?.sort ?? 99;
    if (sa !== sb) return sa - sb;
    return (b.perf5vsSpy ?? -999) - (a.perf5vsSpy ?? -999);
  });
}

const STAGE_ALERT_MAP = {
  [`${STAGE_KEYS.ACCUMULATION}->${STAGE_KEYS.ROTATING}`]: true,
  [`${STAGE_KEYS.ROTATING}->${STAGE_KEYS.HOT}`]: true,
};

export function buildRotationAlerts(prevByEtf, newByEtf, sectorNames) {
  const alerts = [];
  const now = new Date().toISOString();
  for (const [etf, entry] of Object.entries(newByEtf || {})) {
    const prev = prevByEtf?.[etf];
    if (!prev || !entry) continue;
    const from = prev.stageKey;
    const to = entry.stageKey;
    const key = `${from}->${to}`;
    if (!STAGE_ALERT_MAP[key]) continue;
    const plays = SECTOR_TICKER_PLAYS[etf] || [];
    alerts.push({
      id: `${etf}_${Date.now()}`,
      sector: sectorNames[etf] || entry.name || etf,
      etf,
      from_stage: from,
      to_stage: to,
      date: now,
      cmf: entry.cmf,
      performance_vs_spy_5d: entry.perf5vsSpy,
      suggested_tickers: plays.slice(0, 4),
    });
  }
  return alerts;
}

export function buildSectorTradeRecommendation(entry) {
  if (!entry) return "";
  const sector = entry.name || entry.etf;
  const plays = (SECTOR_TICKER_PLAYS[entry.etf] || []).slice(0, 2);
  const t1 = plays[0] || "—";
  const t2 = plays[1] || "—";
  const cmfStr = entry.cmf != null ? (entry.cmf >= 0 ? "+" : "") + entry.cmf.toFixed(2) : "—";

  switch (entry.stageKey) {
    case STAGE_KEYS.ACCUMULATION:
      return `Early rotation detected in ${sector}.\nConsider adding ${t1}, ${t2} to your watchlist now.\nWait for Stage 2 confirmation before entering positions.\nSuggested action: Add to watchlist, set price alerts at 20-day EMA.`;
    case STAGE_KEYS.ROTATING:
      return `${sector} rotation is building.\nTop plays: ${t1} (CMF ${cmfStr}, above EMA21), ${t2} (VCP forming)\nSuggested action: Consider small starter positions. Scale in as rotation confirms.`;
    case STAGE_KEYS.HOT:
      return `${sector} is in active rotation.\nInstitutional money flowing in.\nBest setups: ${t1} (BUY NOW), ${t2} (CALLS SETUP)\nSuggested action: Full position on confirmed setups. Sector tailwind adds conviction to all signals.`;
    case STAGE_KEYS.COOLING:
      return `${sector} rotation may be ending.\nReduce exposure, tighten stops.\nConsider taking profits on open positions.`;
    case STAGE_KEYS.OUT:
      return `Avoid new positions in ${sector}.\nInstitutional money leaving.\nExit any open positions on strength.`;
    default:
      if (entry.perf5vsSpy != null && entry.perf5vsSpy > 5) {
        const pct = entry.perf5vsSpy.toFixed(1);
        return `${sector} is showing strong outperformance vs SPY (+${pct}% over 5 days). Despite neutral CMF, price action suggests rotation may be beginning. Consider adding top tickers to watchlist and scanning for individual setups.`;
      }
      return `${sector} is neutral — monitor for accumulation signals before acting.`;
  }
}

export function attachSectorContextToResult(sym, result, sectorDashboard, tickerSectorMap = {}) {
  if (!result) return result;
  const ticker = String(sym).toUpperCase();
  let rot = getTickerRotationSectorInfo(ticker);
  if (rot.sectorEtf == null) {
    let sectorEtf = tickerSectorMap[ticker] || null;
    if (!sectorEtf) {
      for (const [e, list] of Object.entries(SECTOR_TICKER_PLAYS)) {
        if (list.includes(ticker)) { sectorEtf = e; break; }
      }
    }
    if (sectorEtf) {
      const info = SECTOR_DASHBOARD_ETFS.find(s => s.etf === sectorEtf);
      rot = {
        sector: info?.name || sectorEtf,
        sectorEtf,
        sectorEmoji: SECTOR_EMOJI_BY_ETF[sectorEtf] || "🔄",
      };
    }
  }
  result.sector = rot.sector;
  result.sectorEtf = rot.sectorEtf;
  result.sectorEmoji = rot.sectorEmoji;
  if (rot.sectorEtf && sectorDashboard?.byEtf?.[rot.sectorEtf]) {
    const sectorEntry = sectorDashboard.byEtf[rot.sectorEtf];
    result.sectorStage = sectorEntry.stageKey;
    result.sectorName = sectorEntry.name;
    result.sectorVsSpy5d = sectorEntry.perf5vsSpy ?? null;
  } else {
    result.sectorStage = rot.sector === "Unknown" ? null : null;
    result.sectorName = rot.sector === "Unknown" ? null : rot.sector;
    result.sectorVsSpy5d = null;
  }
  return result;
}

export function getSectorRotationActionSuffix(result) {
  if (!result || result.sector === "Unknown" || !result.sectorStage) return "";
  const stage = result.sectorStage;
  const emoji = result.sectorEmoji || SECTOR_EMOJI_BY_ETF[result.sectorEtf] || "";
  if (stage === STAGE_KEYS.HOT) return ` · ${emoji} HOT SECTOR`;
  if (stage === STAGE_KEYS.ROTATING) return " · 🔄 ROTATING IN";
  if (stage === STAGE_KEYS.ACCUMULATION) return " · 📈 ACCUMULATING";
  return "";
}

export function getSectorRotationHeadwindWarning(result) {
  if (!result || result.sector === "Unknown") return null;
  if (result.sectorStage !== STAGE_KEYS.OUT) return null;
  return "⚠ SECTOR HEADWIND";
}

export function formatStageAlertMessage(alert) {
  const fromLabel = STAGE_DISPLAY[alert.from_stage]?.label || alert.from_stage;
  const toLabel = STAGE_DISPLAY[alert.to_stage]?.label || alert.to_stage;
  const cmf = alert.cmf != null ? (alert.cmf >= 0 ? "+" : "") + alert.cmf.toFixed(2) : "—";
  const perf = alert.performance_vs_spy_5d != null
    ? `${alert.performance_vs_spy_5d >= 0 ? "+" : ""}${alert.performance_vs_spy_5d.toFixed(1)}%`
    : "—";
  const tickers = (alert.suggested_tickers || []).join(", ");
  return `🚨 ROTATION ALERT: ${alert.sector} (${alert.etf}) just moved from ${fromLabel} to ${toLabel}.\nCMF: ${cmf} | 5-day vs SPY: ${perf}\nConsider: ${tickers}`;
}
